import { useState, useCallback } from "react"
import type { GuidebookProperty, DoorCodeVisibilityHint } from "./types"

interface Props {
  property: GuidebookProperty
  checkinTime?: string
  checkoutTime?: string
  doorLocked?: boolean
  wifiLocked?: boolean
  doorCodeVisibility?: DoorCodeVisibilityHint
  doorCodePolicyText?: string
  onUnlock?: () => void
}

export function AccessEssentials({
  property,
  checkinTime,
  doorLocked,
  wifiLocked,
  doorCodeVisibility,
  doorCodePolicyText,
  onUnlock,
}: Props) {
  const [copied, setCopied] = useState<string | null>(null)

  const copy = useCallback((text: string, field: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(field)
      setTimeout(() => setCopied(null), 2000)
    }).catch(() => {})
  }, [])

  const safeCode = typeof property.door_code === "object" ? "" : String(property.door_code || "")
  const doorStatus = property.door_code_status

  const hasDoor = Boolean(doorLocked || safeCode || doorStatus === "pending")
  const hasWifi = Boolean(property.wifi_name || wifiLocked)

  // Don't render if there's nothing to show
  if (!hasDoor && !hasWifi) return null

  // If everything is locked, show a single locked card
  const allLocked = doorLocked && wifiLocked && !property.wifi_name
  if (allLocked) {
    const hint = doorCodeVisibility || "locked"
    const title = hint === "available_on_arrival" ? "Available on arrival" : "Unlock to view"
    const fallbackText = hint === "available_on_arrival"
      ? "Your entry code and Wi-Fi details will appear here on arrival day."
      : "Confirm your booking to reveal your private access details."

    return (
      <div className="gb-section">
        <h2 className="gb-section__title">Access</h2>
        <div className="gb-card gb-card--locked">
          <div className="gb-lock__title">{title}</div>
          <div className="gb-lock__body">{doorCodePolicyText || fallbackText}</div>
          {onUnlock && (
            <button className="gb-copy-btn gb-lock__button" type="button" onClick={onUnlock}>
              Unlock stay details
            </button>
          )}
        </div>
      </div>
    )
  }

  const doorPending = doorStatus === "pending"
  const doorHidden = doorStatus === "checkout" || doorStatus === "disabled" || doorStatus === "not_configured"

  const todayStr = new Date().toISOString().slice(0, 10)
  const availableDate = property.door_code_available_date
  const isAvailableToday = availableDate ? availableDate === todayStr : true

  const hasEssentials = (hasDoor && !doorHidden) || hasWifi
  if (!hasEssentials) return null

  return (
    <div className="gb-section">
      <h2 className="gb-section__title">Access</h2>

      <div className="gb-card gb-access-card">
        {/* Door code */}
        {hasDoor && !doorHidden && (
          <>
            {doorLocked ? (
              <div className="gb-access-item gb-access-item--locked">
                <span className="gb-access-item__icon"><DoorCodeIcon /></span>
                <div className="gb-access-item__body">
                  <span className="gb-access-item__label">Entry code</span>
                  <span className="gb-access-item__value gb-access-item__value--masked">••••••</span>
                  <span className="gb-access-item__sub">
                    {doorCodePolicyText || "Confirm your booking to reveal"}
                  </span>
                </div>
                {onUnlock && (
                  <button className="gb-access-item__unlock-btn" type="button" onClick={onUnlock}>
                    Unlock
                  </button>
                )}
              </div>
            ) : doorPending ? (
              <div className="gb-access-item gb-access-item--pending">
                <span className="gb-access-item__icon"><DoorCodeIcon /></span>
                <div className="gb-access-item__body">
                  <span className="gb-access-item__label">Entry code</span>
                  <span className="gb-access-item__value gb-access-item__value--masked">••••••</span>
                  <span className="gb-access-item__sub">
                    {isAvailableToday
                      ? `Available from ${property.door_code_available_at || checkinTime || "check-in time"}`
                      : "Available from check-in day"}
                  </span>
                </div>
              </div>
            ) : safeCode ? (
              <div className="gb-access-item">
                <span className="gb-access-item__icon"><DoorCodeIcon /></span>
                <div className="gb-access-item__body">
                  <span className="gb-access-item__label">Entry code</span>
                  <span className="gb-access-item__value gb-access-item__value--code">{safeCode}</span>
                </div>
                <button
                  className={`gb-copy-btn${copied === "door" ? " gb-copy-btn--copied" : ""}`}
                  onClick={() => copy(safeCode, "door")}
                  type="button"
                  aria-label="Copy door code"
                >
                  {copied === "door" ? <CheckIcon /> : "Copy"}
                </button>
              </div>
            ) : null}
          </>
        )}

        {/* Wi-Fi */}
        {hasWifi && (
          <>
            {property.wifi_name && (
              <div className="gb-access-item">
                <span className="gb-access-item__icon"><WifiIcon /></span>
                <div className="gb-access-item__body">
                  <span className="gb-access-item__label">Wi-Fi</span>
                  <span className="gb-access-item__value">{property.wifi_name}</span>
                  {property.wifi_password && !wifiLocked && (
                    <span className="gb-access-item__sub gb-access-item__sub--mono">{property.wifi_password}</span>
                  )}
                  {wifiLocked && (
                    <span className="gb-access-item__sub">Unlock to reveal password</span>
                  )}
                </div>
                {!wifiLocked && property.wifi_password && (
                  <button
                    className={`gb-copy-btn${copied === "wifi" ? " gb-copy-btn--copied" : ""}`}
                    onClick={() => copy(property.wifi_password, "wifi")}
                    type="button"
                    aria-label="Copy Wi-Fi password"
                  >
                    {copied === "wifi" ? <CheckIcon /> : "Copy"}
                  </button>
                )}
                {wifiLocked && onUnlock && (
                  <button className="gb-access-item__unlock-btn" type="button" onClick={onUnlock}>
                    Unlock
                  </button>
                )}
              </div>
            )}
            {!property.wifi_name && wifiLocked && (
              <div className="gb-access-item gb-access-item--locked">
                <span className="gb-access-item__icon"><WifiIcon /></span>
                <div className="gb-access-item__body">
                  <span className="gb-access-item__label">Wi-Fi</span>
                  <span className="gb-access-item__value gb-access-item__value--masked">••••••••</span>
                  <span className="gb-access-item__sub">Confirm your booking to reveal</span>
                </div>
                {onUnlock && (
                  <button className="gb-access-item__unlock-btn" type="button" onClick={onUnlock}>
                    Unlock
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------------------
   Icons
   --------------------------------------------------------------------------- */

function DoorCodeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      <circle cx="12" cy="16.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  )
}

function WifiIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12.55a11 11 0 0 1 14.08 0" />
      <path d="M1.42 9a16 16 0 0 1 21.16 0" />
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
      <circle cx="12" cy="20" r="1" fill="currentColor" stroke="none" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 14, height: 14 }}>
      <path d="M3 8.5l3.5 3.5 6.5-7" />
    </svg>
  )
}
